import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import '@fontsource-variable/geist/wght.css';
import { toYmd } from '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker';
import { fetchTechnicianPanelTrackBusyDays } from '../services/technicianPanelApi';
import '../../users-customers/components/CustomersNoticeScheduleDates.css';

const WEEKDAYS = ['do', '2ª', '3ª', '4ª', '5ª', '6ª', 'sá'];
const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/** Cache em memória: techId|from|to → string[] (evita refetch ao reabrir o mesmo mês). */
const busyDaysCache = new Map();
const BUSY_CACHE_MAX = 24;

function cacheBusyDays(key, days) {
  if (busyDaysCache.size >= BUSY_CACHE_MAX) {
    const first = busyDaysCache.keys().next().value;
    if (first != null) busyDaysCache.delete(first);
  }
  busyDaysCache.set(key, days);
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function cellYmd(cell) {
  return `${cell.year}-${pad2(cell.month + 1)}-${pad2(cell.day)}`;
}

function nextDayYmd(ymd) {
  const d = parseYmd(ymd);
  if (!d) return ymd;
  return toYmd(addDays(d, 1));
}

function parseYmd(ymd) {
  if (!ymd) return null;
  const [y, m, d] = String(ymd).split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function addDays(base, days) {
  return new Date(base.getFullYear(), base.getMonth(), base.getDate() + days);
}

function formatWeekdayShort(date) {
  return date
    .toLocaleDateString('pt-BR', { weekday: 'short' })
    .replace('.', '')
    .toLowerCase();
}

function formatDayMonth(date) {
  const day = date.getDate();
  const mon = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
  return `${day} ${mon}`;
}

function parseTimeInput(raw) {
  const s = String(raw || '').trim().replace(/[hH]/g, ':');
  if (!s) return { empty: true };
  const m = s.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = m[2] != null ? parseInt(m[2], 10) : 0;
  if (!Number.isFinite(h) || h < 0 || h > 23 || !Number.isFinite(min) || min < 0 || min > 59) {
    return null;
  }
  return { hour: pad2(h), minute: pad2(min) };
}

function formatTimeInput(hour, minute) {
  if (hour == null || minute == null || hour === '' || minute === '') return '';
  return `${pad2(hour)}:${pad2(minute)}`;
}

function normalizeHhMm(raw) {
  const parsed = parseTimeInput(raw);
  if (!parsed || parsed.empty) return '';
  return `${parsed.hour}:${parsed.minute}`;
}

function buildMonthCells(year, month) {
  const first = new Date(year, month, 1);
  const leading = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  const prev = new Date(year, month, 0);
  const prevDays = prev.getDate();
  for (let i = leading - 1; i >= 0; i -= 1) {
    cells.push({
      year: prev.getFullYear(),
      month: prev.getMonth(),
      day: prevDays - i,
      outside: true,
    });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ year, month, day, outside: false });
  }
  const next = new Date(year, month + 1, 1);
  while (cells.length < 42) {
    cells.push({
      year: next.getFullYear(),
      month: next.getMonth(),
      day: next.getDate(),
      outside: true,
    });
    next.setDate(next.getDate() + 1);
  }
  return cells;
}

function getTrackShortcuts(now = new Date()) {
  const yesterday = addDays(now, -1);
  const d2 = addDays(now, -2);
  const d3 = addDays(now, -3);
  const weekAgo = addDays(now, -7);
  const twoWeeks = addDays(now, -14);
  return [
    {
      id: 'today',
      label: 'Hoje',
      hint: formatWeekdayShort(now),
      apply: () => toYmd(now),
    },
    {
      id: 'yesterday',
      label: 'Ontem',
      hint: formatWeekdayShort(yesterday),
      apply: () => toYmd(yesterday),
    },
    {
      id: 'd2',
      label: 'Anteontem',
      hint: formatDayMonth(d2),
      apply: () => toYmd(d2),
    },
    {
      id: 'd3',
      label: 'Há 3 dias',
      hint: formatDayMonth(d3),
      apply: () => toYmd(d3),
    },
    {
      id: 'week',
      label: 'Há 1 semana',
      hint: formatDayMonth(weekAgo),
      apply: () => toYmd(weekAgo),
    },
    {
      id: '2w',
      label: 'Há 2 semanas',
      hint: formatDayMonth(twoWeeks),
      apply: () => toYmd(twoWeeks),
    },
  ];
}

function ymdInBounds(ymd, minDate, maxDate) {
  if (!ymd) return false;
  if (minDate && ymd < minDate) return false;
  if (maxDate && ymd > maxDate) return false;
  return true;
}

/**
 * Popover data (+ horário opcional) — visual CMC.
 * @param {boolean} [hideTime] — só calendário (Produtividade); sem linha de horário.
 */
export default function TechnicianTrackDateTimePopover({
  anchorRect,
  currentDate,
  timeFrom = '',
  timeTo = '',
  minDate = null,
  maxDate = null,
  technicianId = null,
  hideTime = false,
  onApply,
  onClear,
  onClose,
  zIndex = 14050,
}) {
  const modalRef = useRef(null);
  const seed = parseYmd(currentDate) || new Date();
  const [selectedDate, setSelectedDate] = useState(() =>
    currentDate && /^\d{4}-\d{2}-\d{2}$/.test(currentDate) ? currentDate : toYmd(new Date())
  );
  const [fromText, setFromText] = useState(() => normalizeHhMm(timeFrom));
  const [toText, setToText] = useState(() => normalizeHhMm(timeTo));
  const [viewMonth, setViewMonth] = useState(() => ({
    year: seed.getFullYear(),
    month: seed.getMonth(),
  }));
  const [timeError, setTimeError] = useState('');
  const [busyByDay, setBusyByDay] = useState(() => new Set());

  const shortcuts = useMemo(() => getTrackShortcuts(new Date()), []);
  const cells = useMemo(
    () => buildMonthCells(viewMonth.year, viewMonth.month),
    [viewMonth.year, viewMonth.month]
  );
  const todayYmd = toYmd(new Date());

  useEffect(() => {
    const tech = technicianId != null ? String(technicianId).trim() : '';
    if (!tech || !cells.length) {
      setBusyByDay(new Set());
      return undefined;
    }
    const from = cellYmd(cells[0]);
    const to = nextDayYmd(cellYmd(cells[cells.length - 1]));
    const cacheKey = `${tech}|${from}|${to}`;
    const cached = busyDaysCache.get(cacheKey);
    if (cached) {
      setBusyByDay(new Set(cached));
      return undefined;
    }

    const ac = new AbortController();
    let cancelled = false;
    fetchTechnicianPanelTrackBusyDays(tech, from, to, { signal: ac.signal })
      .then((days) => {
        if (cancelled) return;
        cacheBusyDays(cacheKey, days);
        setBusyByDay(new Set(days));
      })
      .catch((err) => {
        if (cancelled || err?.name === 'AbortError') return;
        setBusyByDay(new Set());
      });
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [technicianId, cells]);

  const position = useMemo(() => {
    if (!anchorRect) return null;
    const width = Math.min(448, window.innerWidth - 24);
    let left = anchorRect.left;
    if (left + width > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - width - 12);
    }
    const spaceBelow = window.innerHeight - anchorRect.bottom - 12;
    if (spaceBelow >= 300) {
      return { top: anchorRect.bottom + 8, left, bottom: 'auto', width };
    }
    return {
      bottom: window.innerHeight - anchorRect.top + 8,
      left,
      top: 'auto',
      width,
    };
  }, [anchorRect]);

  useEffect(() => {
    const onDoc = (e) => {
      if (modalRef.current?.contains(e.target)) return;
      onClose?.();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  const selectDate = (ymd) => {
    if (!ymdInBounds(ymd, minDate, maxDate)) return;
    setSelectedDate(ymd);
    const d = parseYmd(ymd);
    if (d) setViewMonth({ year: d.getFullYear(), month: d.getMonth() });
  };

  const commit = () => {
    if (!selectedDate) return;
    if (hideTime) {
      onApply?.({ date: selectedDate, timeFrom: '', timeTo: '' });
      onClose?.();
      return;
    }
    const fromParsed = parseTimeInput(fromText);
    const toParsed = parseTimeInput(toText);
    if (fromParsed === null || toParsed === null) {
      setTimeError('Horário inválido. Use HH:mm.');
      return;
    }
    const nextFrom = fromParsed.empty ? '' : `${fromParsed.hour}:${fromParsed.minute}`;
    const nextTo = toParsed.empty ? '' : `${toParsed.hour}:${toParsed.minute}`;
    if (nextFrom && nextTo && nextFrom > nextTo) {
      setTimeError('O horário inicial deve ser menor ou igual ao final.');
      return;
    }
    setTimeError('');
    onApply?.({ date: selectedDate, timeFrom: nextFrom, timeTo: nextTo });
    onClose?.();
  };

  if (!position) return null;

  return createPortal(
    <div
      ref={modalRef}
      className={`cmc-notice-dates-float tpm-track-datetime-float${hideTime ? ' is-date-only' : ''}`}
      style={{
        position: 'fixed',
        zIndex,
        width: position.width,
        ...(position.top != null && position.top !== 'auto' ? { top: `${position.top}px` } : { top: 'auto' }),
        ...(position.bottom != null && position.bottom !== 'auto'
          ? { bottom: `${position.bottom}px` }
          : { bottom: 'auto' }),
        left: `${position.left}px`,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="cmc-notice-dates-popover"
        role="dialog"
        aria-modal="true"
        aria-label={hideTime ? 'Dia da produtividade' : 'Data e horário do rastreio'}
      >
        {!hideTime ? (
          <div className="cmc-notice-dates-head tpm-track-datetime-head">
            <div className="cmc-notice-dates-time-row tpm-track-datetime-time-row">
              <input
                className="cmc-notice-dates-time-input"
                value={fromText}
                onChange={(e) => {
                  setFromText(e.target.value);
                  setTimeError('');
                }}
                onBlur={() => {
                  const parsed = parseTimeInput(fromText);
                  if (parsed && !parsed.empty) {
                    setFromText(formatTimeInput(parsed.hour, parsed.minute));
                  } else if (!String(fromText || '').trim()) {
                    setFromText('');
                  }
                }}
                aria-label="Horário inicial"
                placeholder="00:00"
              />
              <span className="cmc-notice-dates-time-para">até</span>
              <input
                className="cmc-notice-dates-time-input"
                value={toText}
                onChange={(e) => {
                  setToText(e.target.value);
                  setTimeError('');
                }}
                onBlur={() => {
                  const parsed = parseTimeInput(toText);
                  if (parsed && !parsed.empty) {
                    setToText(formatTimeInput(parsed.hour, parsed.minute));
                  } else if (!String(toText || '').trim()) {
                    setToText('');
                  }
                }}
                aria-label="Horário final"
                placeholder="23:59"
              />
            </div>
          </div>
        ) : null}

        {!hideTime && timeError ? (
          <p className="tpm-track-datetime-err" role="alert">
            {timeError}
          </p>
        ) : null}

        <div className="cmc-notice-dates-body">
          <aside className="cmc-notice-dates-shortcuts">
            {shortcuts.map((item) => {
              const ymd = item.apply();
              const disabled = !ymdInBounds(ymd, minDate, maxDate);
              return (
                <button
                  key={item.id}
                  type="button"
                  className="cmc-notice-dates-shortcut"
                  disabled={disabled}
                  onClick={() => selectDate(ymd)}
                >
                  <span>{item.label}</span>
                  <span className="cmc-notice-dates-shortcut-hint">{item.hint}</span>
                </button>
              );
            })}
          </aside>

          <div className="cmc-notice-dates-calendar">
            <div className="cmc-notice-dates-cal-head">
              <div className="cmc-notice-dates-cal-title">
                {MONTHS[viewMonth.month]} {viewMonth.year}
              </div>
              <div className="cmc-notice-dates-cal-nav">
                <button
                  type="button"
                  className="cmc-notice-dates-today-btn"
                  onClick={() => {
                    const n = new Date();
                    setViewMonth({ year: n.getFullYear(), month: n.getMonth() });
                    selectDate(toYmd(n));
                  }}
                >
                  Hoje
                </button>
                <button
                  type="button"
                  className="cmc-notice-dates-nav-btn"
                  aria-label="Mês anterior"
                  onClick={() =>
                    setViewMonth((v) => {
                      const d = new Date(v.year, v.month - 1, 1);
                      return { year: d.getFullYear(), month: d.getMonth() };
                    })
                  }
                >
                  <ChevronUp size={15} strokeWidth={2} />
                </button>
                <button
                  type="button"
                  className="cmc-notice-dates-nav-btn"
                  aria-label="Próximo mês"
                  onClick={() =>
                    setViewMonth((v) => {
                      const d = new Date(v.year, v.month + 1, 1);
                      return { year: d.getFullYear(), month: d.getMonth() };
                    })
                  }
                >
                  <ChevronDown size={15} strokeWidth={2} />
                </button>
              </div>
            </div>

            <div className="cmc-notice-dates-weekdays">
              {WEEKDAYS.map((w) => (
                <span key={w}>{w}</span>
              ))}
            </div>
            <div className="cmc-notice-dates-days">
              {cells.map((cell, idx) => {
                const ymd = cellYmd(cell);
                const isSelected = selectedDate === ymd;
                const isToday = todayYmd === ymd;
                const disabled = !ymdInBounds(ymd, minDate, maxDate);
                const dayBusy = busyByDay.has(ymd);
                return (
                  <button
                    key={`${ymd}-${idx}`}
                    type="button"
                    disabled={disabled}
                    className={[
                      'cmc-notice-dates-day',
                      cell.outside ? 'is-outside' : '',
                      isSelected ? 'is-selected is-range-start is-range-end' : '',
                      isToday && !isSelected ? 'is-today' : '',
                      dayBusy ? 'has-busy' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => selectDate(ymd)}
                  >
                    {cell.day}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="cmc-notice-dates-footer">
          <div className="cmc-notice-dates-actions">
            <button
              type="button"
              className="user-form-link-btn user-form-footer-btn"
              onClick={() => {
                onClear?.();
                onClose?.();
              }}
            >
              Limpar
            </button>
            <button
              type="button"
              className="user-form-profile-save-btn user-form-footer-btn"
              disabled={!selectedDate}
              onClick={commit}
            >
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
